import { Button, Dialog, DialogBody, DialogTitle } from "@/components";
import { PayWithWalletProps, usePayWithWallet } from "./usePayWithWallet";
import {
  ArrowRightCircleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import FundWalletModal from "@/app/(procure)/procure/wallet/_components/FundWallet";
import CreateWalletModal from "../CreateWallet";
import { useStoreContext } from "@/lib/store";
import { formatCurrency } from "@centisystems/lib/utils";

export function PayWithWallet(
  props: PayWithWalletProps & {
    button?: string | null;
    full: boolean;
    disabled?: boolean;
    onDisabledClick?: () => void;
  },
) {
  const { button, setIsOpen, isOpen } = props;
  const {
    handlePayment,
    hasWallet,
    wallet,
    lowBalance,
    amountBreakdown,
    isLoading,
    isProcessingPayment,
    isValidating,
    openModal,
    closeModal,
    modalIsOpen,
  } = usePayWithWallet(props);
  const { isMaxSm } = useStoreContext();

  return (
    <>
      {button && (
        <Button
          full={props.full}
          color="indigo"
          className="!h-11 md:!h-14 md:!text-sm aria-disabled:!cursor-not-allowed aria-disabled:opacity-50"
          loading={isLoading}
          disabled={isLoading}
          aria-disabled={props.disabled}
          data-disabled={props.disabled ? "" : undefined}
          onClick={() => {
            if (props.disabled) {
              props.onDisabledClick?.();
              return;
            }
            setIsOpen(true);
          }}>
          {button}
        </Button>
      )}
      {!hasWallet ? (
        <CreateWalletModal
          handleClose={() => setIsOpen(false)}
          handleOpen={() => setIsOpen}
          isOpen={isOpen}
        />
      ) : (
        <>
          <Dialog
            size="5xl"
            centered
            open={isOpen}
            onClose={setIsOpen}
            className={"md:!w-[500px] !min-w-[310px] !w-full"}>
            <DialogTitle className="flex justify-between items-center">
              Confirm your payment
              <XMarkIcon
                className="h-6 w-6"
                onClick={() => {
                  setIsOpen(false);
                }}
              />
            </DialogTitle>
            <DialogBody className="space-y-3">
              {lowBalance && (
                <div className="bg-primary-light border border-primary py-1 px-2 rounded-lg flex justify-between items-center">
                  <p className="flex items-center text-dark max-sm:text-sm">
                    <ExclamationCircleIcon className="size-5 mr-1" />
                    Your wallet balance is low
                  </p>
                  <Button plain onClick={() => openModal()}>
                    {isMaxSm ? "Fund" : "Fund Wallet"}
                  </Button>
                </div>
              )}
              <div className="border rounded-lg p-4 space-y-4">
                {Object.keys(amountBreakdown?.breakdown ?? {}).map((key) => (
                  <div className="flex justify-between" key={key}>
                    <p className="text-gray-500">
                      {amountBreakdown?.breakdown?.[key]?.title}
                      <br />
                      <small>
                        {amountBreakdown?.breakdown?.[key]?.description}
                      </small>
                    </p>
                    <p>
                      {formatCurrency(
                        amountBreakdown?.breakdown?.[key]?.amount ?? 0,
                      )}
                    </p>
                  </div>
                ))}
                <div className="flex justify-between">
                  <p className="text-gray-500">Total</p>
                  <p>{formatCurrency(amountBreakdown?.amount ?? 0)}</p>
                </div>
              </div>
              <div className="border rounded-lg p-4 space-y-3">
                <h4 className="font-semibold">Centi Wallet</h4>
                <div className="flex justify-between">
                  <p className="text-gray-500">Balance</p>
                  <p>{formatCurrency(wallet?.balance ?? 0)}</p>
                </div>
              </div>
              <Button
                color="dark"
                className="!h-14 !w-full"
                onClick={() => handlePayment()}
                loading={isProcessingPayment || isLoading || isValidating}
                disabled={
                  props.disabled ||
                  lowBalance ||
                  isLoading ||
                  isValidating ||
                  isProcessingPayment
                }>
                {!lowBalance && <CheckCircleIcon className="!text-green-600" />}
                Confirm Payment
              </Button>
            </DialogBody>
          </Dialog>
          <FundWalletModal
            wallet={wallet!}
            handleOpen={openModal}
            handleClose={closeModal}
            isOpen={modalIsOpen}
          />
        </>
      )}
    </>
  );
}
